import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface EstabelecimentoOption {
    id: number
    nome: string
}

export interface PromocaoOption {
    id: number
    premio: string
}

export interface SituacaoOption {
    id: number
    descricao: string
}

export interface CadastroAplicativo {
    appNome: string
    urlAppAndroid: string | null
    urlAppIphone: string | null
    codigoFireBase: string | null
    urlAcesso: string | null
    urlAcesso2: string | null
    urlAcesso3: string | null
    urlArtesDivulgacao: string | null
    packageName: string | null
    categorias: string | null
    appNomeAlternativo: string | null
    situacao: number
    ativo: boolean
    idEstabelecimentoPrincipal: number | null
    nomeEstabelecimentoPrincipal: string | null
    idPromocaoPrincipal: number | null
    nomePromocaoPrincipal: string | null
    dataPrevisaoLancamento: string | null
    dataLancamento: string | null
    estabelecimentos: EstabelecimentoOption[]
    promocoes: PromocaoOption[]
    situacaoOptions: SituacaoOption[]
    podeEditar?: boolean
}

export interface AtualizarCadastroPayload {
    urlAppAndroid: string | null
    urlAppIphone: string | null
    codigoFireBase: string | null
    urlAcesso: string | null
    urlAcesso2: string | null
    urlAcesso3: string | null
    urlArtesDivulgacao: string | null
    packageName: string | null
    categorias: string | null
    appNomeAlternativo: string | null
    situacao: number
    ativo: boolean
    idEstabelecimentoPrincipal: number | null
    idPromocaoPrincipal: number | null
    dataPrevisaoLancamento: string | null
    dataLancamento: string | null
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }
type RespostaErroRaw = { codErro: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

export const obterCadastro = async function (
    authorization: string
): Promise<CadastroAplicativo | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<CadastroAplicativo>>("aplicativoDados/ObterCadastroAplicativo", {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authorization,
                },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<CadastroAplicativo>(response, "ObterCadastroAplicativo")
    } catch (error) {
        RegistrarLogErro(error, "ObterCadastroAplicativo", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const listarPromocoesEstabelecimento = async function (
    authorization: string,
    idEstabelecimento: number
): Promise<PromocaoOption[] | ErroApi> {
    try {
        const response = await api
            .get<PromocaoOption[] | RespostaErroRaw>(
                `aplicativoDados/ListarPromocoesEstabelecimento/${idEstabelecimento}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return (response.data as PromocaoOption[]) ?? []
    } catch (error) {
        RegistrarLogErro(error, "ListarPromocoesEstabelecimento", String(idEstabelecimento))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarCadastro = async function (
    authorization: string,
    payload: AtualizarCadastroPayload
): Promise<CadastroAplicativo | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<CadastroAplicativo>>(
                "aplicativoDados/AtualizarCadastroAplicativo",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<CadastroAplicativo>(response, "AtualizarCadastroAplicativo")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarCadastroAplicativo", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
